import {
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MOBILE_MEDIA_QUERY } from "../../../constants/mediaQueries";
import { setActiveOU } from "../../../redux/actions/authActions";
import { styledDialogChildSX } from "../../../styled/styledSXProperties/styledDialogueSX";
import { baseStyledPaperSX } from "../../../styled/styledSXProperties/styledPaperSX";
import { dateFormat } from "../../../utils/general";
import CustomDialogTitle from "../../customDialog/customDialogTitle";

/**
 * Disambiguation modal shown when a PO_NUMBER resolves to more than one
 * Operating Unit the current user can access (ProcurementAPI HTTP 300).
 *
 * Lets the user pick the OU whose PO they want to view. On confirm we:
 *   1. Dispatch `setActiveOU(ORG_NAME)` so the rest of the app stays
 *      OU-scoped consistently after the disambiguation.
 *   2. Persist the OU in localStorage (parity with the user-profile OU
 *      switcher in `userProfileContent.js`).
 *   3. Invoke `onSelect(match)` so the parent page can re-issue the PO
 *      fetch with the resolved `ORG_ID`.
 */
const PoOuChooserDialog = ({
    isOpen,
    poNumber,
    matches,
    onSelect,
    onClose,
}) => {
    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);
    const dispatch = useDispatch();
    const darkMode = useSelector((state) => state.general.darkMode);

    const [selectedOrgId, setSelectedOrgId] = useState("");

    // Pre-select the first option whenever the matches list changes so the
    // confirm button is immediately actionable.
    useEffect(() => {
        if (matches && matches.length > 0) {
            setSelectedOrgId(String(matches[0].ORG_ID));
        } else {
            setSelectedOrgId("");
        }
    }, [matches]);

    const handleClose = (e_, reason) => {
        if (reason && reason === "backdropClick") return;
        onClose();
    };

    const handleConfirm = () => {
        const chosen = matches.find((m) => String(m.ORG_ID) === selectedOrgId);
        if (!chosen) return;
        localStorage.setItem("OU", chosen.ORG_NAME);
        dispatch(setActiveOU(chosen.ORG_NAME));
        onSelect(chosen);
    };

    return (
        <Dialog
            fullWidth
            maxWidth="xs"
            onClose={handleClose}
            open={isOpen}
            fullScreen={!isDesktop}
            PaperProps={{ sx: baseStyledPaperSX(darkMode) }}
            aria-labelledby="po-ou-chooser-title"
        >
            <CustomDialogTitle
                onClose={handleClose}
                tooltip="Close OU chooser"
                title="Select Operating Unit"
                subtitle={
                    poNumber
                        ? `PO ${poNumber} exists in more than one of your Operating Units. Choose which one to open.`
                        : "This PO exists in more than one of your Operating Units. Choose which one to open."
                }
            />
            <DialogContent sx={styledDialogChildSX}>
                <Stack spacing={2}>
                    <FormControl>
                        <RadioGroup
                            aria-label="operating-unit"
                            name="po-ou-chooser-radio-group"
                            value={selectedOrgId}
                            onChange={(e) => setSelectedOrgId(e.target.value)}
                        >
                            {(matches || []).map((m) => (
                                <FormControlLabel
                                    key={m.ORG_ID}
                                    value={String(m.ORG_ID)}
                                    control={
                                        <Radio
                                            sx={{
                                                // Unselected: greyed out.
                                                color: "text.disabled",
                                                // Selected: same blue as the
                                                // "Open PO" action button.
                                                "&.Mui-checked": {
                                                    color: "secondary.main",
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Stack>
                                            <Typography variant="body1">
                                                {m.ORG_NAME}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {[
                                                    m.SUPPLIER_SITE_CODE &&
                                                        `Supplier Site ${m.SUPPLIER_SITE_CODE}`,
                                                    m.AUTHORIZATION_STATUS,
                                                    m.CREATION_DATE &&
                                                        `Created ${dateFormat(
                                                            m.CREATION_DATE
                                                        )}`,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={styledDialogChildSX}>
                <Button onClick={handleClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="secondary"
                    disabled={!selectedOrgId}
                >
                    Open PO
                </Button>
            </DialogActions>
        </Dialog>
    );
};

PoOuChooserDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    poNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    matches: PropTypes.arrayOf(
        PropTypes.shape({
            ORG_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            ORG_NAME: PropTypes.string.isRequired,
            PO_HEADER_ID: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            SUPPLIER_SITE_CODE: PropTypes.string,
            AUTHORIZATION_STATUS: PropTypes.string,
            CREATION_DATE: PropTypes.string,
        })
    ).isRequired,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default PoOuChooserDialog;
